import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { InputText } from "primereact/inputtext";
import { Menu } from "primereact/menu";
import { PanelMenu } from "primereact/panelmenu";
import { Sidebar } from "primereact/sidebar";
import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "./Header.css";

const ROOT_PATH = "https://hambler-api.onbeta.in/v1/";
const MENU_API_URL = `${ROOT_PATH}product/getmenucategories`;
const SEARCH_TRENDS_API_URL = `${ROOT_PATH}product/searchtrends`;

const normalizeUrl = (path = "") => {
  const cleanedPath = `${path}`.trim();

  if (!cleanedPath) {
    return "#";
  }

  return cleanedPath;
};

const sortByOrder = (items = []) =>
  [...items].sort(
    (firstItem, secondItem) =>
      Number(firstItem?.OrderBy ?? 0) - Number(secondItem?.OrderBy ?? 0)
  );

const buildMenuTree = (items = [], branchKey = "menu") =>
  sortByOrder(items).map((item, index) => {
    const label = item?.Name?.trim() || "Menu";
    const id = `${branchKey}-${item?.Id ?? index}-${label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")}`;
    const children = buildMenuTree(item?.ChildMenu || [], id);

    return {
      id,
      label,
      url: normalizeUrl(item?.Url),
      children,
    };
  });

const buildSearchTrends = (items = []) =>
  items.map((item, index) => ({
    id: `trend-${item?.Serial ?? index}`,
    label: item?.SearchText?.trim() || `Product ${index + 1}`,
    url: normalizeUrl(item?.ResultURL),
  }));

const createPanelMenuItems = (items = [], onLeafSelect) =>
  items.map((item) => {
    const hasChildren = item.children.length > 0;

    return {
      id: item.id,
      label: item.label,
      url: hasChildren ? undefined : item.url,
      items: hasChildren ? createPanelMenuItems(item.children, onLeafSelect) : undefined,
      command: hasChildren ? undefined : () => onLeafSelect?.(),
    };
  });

function Header() {
  const headerRef = useRef(null);
  const searchRef = useRef(null);
  const accountMenuRef = useRef(null);
  const desktopMenuCloseTimerRef = useRef(null);

  const [menuItems, setMenuItems] = useState([]);
  const [searchTrends, setSearchTrends] = useState([]);
  const [isMenuLoading, setIsMenuLoading] = useState(true);
  const [isSearchLoading, setIsSearchLoading] = useState(true);
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [openDesktopMenuId, setOpenDesktopMenuId] = useState(null);
  const [activeDesktopBranchId, setActiveDesktopBranchId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  useEffect(() => {
    const existingFontAwesomeLink = document.querySelector(
      'link[data-header-fontawesome="local"]'
    );

    if (existingFontAwesomeLink) {
      return;
    }

    const fontAwesomeLink = document.createElement("link");
    fontAwesomeLink.rel = "stylesheet";
    fontAwesomeLink.href = "/assets/css/all.min.css";
    fontAwesomeLink.setAttribute("data-header-fontawesome", "local");
    document.head.appendChild(fontAwesomeLink);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchMenuItems = async () => {
      try {
        const response = await fetch(MENU_API_URL);
        const data = await response.json();

        if (!isMounted) {
          return;
        }

        if (data?.Status && Array.isArray(data?.Result)) {
          setMenuItems(buildMenuTree(data.Result));
        } else {
          setMenuItems([]);
        }
      } catch (error) {
        console.error("Unable to fetch menu categories:", error);

        if (isMounted) {
          setMenuItems([]);
        }
      } finally {
        if (isMounted) {
          setIsMenuLoading(false);
        }
      }
    };

    const fetchSearchTrends = async () => {
      try {
        const response = await fetch(SEARCH_TRENDS_API_URL);
        const data = await response.json();

        if (!isMounted) {
          return;
        }

        if (data?.Status && Array.isArray(data?.Result)) {
          setSearchTrends(buildSearchTrends(data.Result));
        } else {
          setSearchTrends([]);
        }
      } catch (error) {
        console.error("Unable to fetch search trends:", error);

        if (isMounted) {
          setSearchTrends([]);
        }
      } finally {
        if (isMounted) {
          setIsSearchLoading(false);
        }
      }
    };

    fetchMenuItems();
    fetchSearchTrends();

    return () => {
      isMounted = false;

      if (desktopMenuCloseTimerRef.current) {
        clearTimeout(desktopMenuCloseTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!searchRef.current?.contains(event.target)) {
        setIsSearchOpen(false);
      }

      if (!headerRef.current?.contains(event.target)) {
        setOpenDesktopMenuId(null);
        setActiveDesktopBranchId(null);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setOpenDesktopMenuId(null);
        setActiveDesktopBranchId(null);
        setIsSearchOpen(false);
        setIsSidebarVisible(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const cancelDesktopMenuClose = () => {
    if (desktopMenuCloseTimerRef.current) {
      clearTimeout(desktopMenuCloseTimerRef.current);
      desktopMenuCloseTimerRef.current = null;
    }
  };

  const scheduleDesktopMenuClose = () => {
    cancelDesktopMenuClose();
    desktopMenuCloseTimerRef.current = setTimeout(() => {
      setOpenDesktopMenuId(null);
      setActiveDesktopBranchId(null);
    }, 140);
  };

  const handleDesktopMenuOpen = (menuItem) => {
    cancelDesktopMenuClose();
    setIsSearchOpen(false);

    if (!menuItem?.children?.length) {
      setOpenDesktopMenuId(null);
      setActiveDesktopBranchId(null);
      return;
    }

    setOpenDesktopMenuId(menuItem.id);
    setActiveDesktopBranchId(null);
  };

  const activeDesktopMenu = menuItems.find((menuItem) => menuItem.id === openDesktopMenuId) || null;
  const activeDesktopBranch =
    activeDesktopMenu?.children.find((branch) => branch.id === activeDesktopBranchId) || null;

  const filteredTrends = searchTerm.trim()
    ? searchTrends
        .filter((trend) =>
          trend.label.toLowerCase().includes(searchTerm.trim().toLowerCase())
        )
        .slice(0, 12)
    : searchTrends.slice(0, 12);

  const accountMenuItems = [
    {
      label: "Profile",
      icon: "pi pi-user",
      command: () => null,
    },
    {
      label: "Inbox",
      icon: "pi pi-envelope",
      command: () => null,
    },
    {
      label: "Help",
      icon: "pi pi-question-circle",
      command: () => null,
    },
    {
      label: "Sign Out",
      icon: "pi pi-sign-out",
      command: () => null,
    },
  ];

  const panelMenuItems = createPanelMenuItems(menuItems, () => setIsSidebarVisible(false));

  const handleSearchKeyDown = (event) => {
    if (event.key !== "Enter") {
      return;
    }

    const firstResult = filteredTrends[0];

    if (firstResult?.url && firstResult.url !== "#") {
      window.location.assign(firstResult.url);
      return;
    }

    if (searchTerm.trim()) {
      window.location.assign(`/?s=${encodeURIComponent(searchTerm.trim())}`);
    }
  };

  const renderSearchBox = () => (
    <div
      ref={searchRef}
      className={`header-search ${isSearchOpen ? "is-open" : ""}`}
    >
      <span className="header-search-icon" aria-hidden="true">
        <i className="fa-light fa-magnifying-glass" />
      </span>

      <InputText
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
        onFocus={() => {
          setIsSearchOpen(true);
          setOpenDesktopMenuId(null);
          setActiveDesktopBranchId(null);
        }}
        onClick={() => setIsSearchOpen(true)}
        onKeyDown={handleSearchKeyDown}
        placeholder="Search for products"
        className="header-search-input"
      />

      {isSearchOpen && (
        <div className="header-search-dropdown">
          <div className="header-search-dropdown-title">Trending searches</div>

          <div className="header-search-dropdown-body">
            {isSearchLoading && (
              <div className="header-search-empty">Loading popular searches...</div>
            )}

            {!isSearchLoading && !filteredTrends.length && (
              <div className="header-search-empty">No matching products found.</div>
            )}

            {!isSearchLoading &&
              filteredTrends.map((trend) => (
                <a
                  key={trend.id}
                  className="header-search-trend"
                  href={trend.url}
                  onClick={() => setIsSearchOpen(false)}
                >
                  <span>{trend.label}</span>
                  <i className="pi pi-arrow-up-right" aria-hidden="true" />
                </a>
              ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      <header className="header" ref={headerRef}>
        <div className="header-shell">
          <div className="header-grid">
            <div className="header-brand-cluster">
              <button
                type="button"
                className="header-mobile-toggle"
                onClick={() => {
                  setIsSidebarVisible(true);
                  setIsSearchOpen(false);
                  setOpenDesktopMenuId(null);
                  setActiveDesktopBranchId(null);
                }}
                aria-label="Open navigation menu"
              >
                <i className="fa-light fa-bars" />
              </button>

              <Link className="header-logo" to="/">
                <img src="/assets/images/logo.png" alt="Hambler" />
              </Link>
            </div>

            <div
              className="header-nav-group"
              onMouseEnter={cancelDesktopMenuClose}
              onMouseLeave={scheduleDesktopMenuClose}
            >
              <nav className="header-desktop-nav" aria-label="Desktop navigation">
                {isMenuLoading &&
                  Array.from({ length: 6 }).map((_, index) => (
                    <span key={`header-skeleton-${index}`} className="header-nav-skeleton" />
                  ))}

                {!isMenuLoading &&
                  menuItems.map((menuItem) => (
                    <button
                      key={menuItem.id}
                      type="button"
                      className={`header-nav-link ${
                        openDesktopMenuId === menuItem.id ? "is-active" : ""
                      }`}
                      onMouseEnter={() => handleDesktopMenuOpen(menuItem)}
                      onFocus={() => handleDesktopMenuOpen(menuItem)}
                      onClick={() => handleDesktopMenuOpen(menuItem)}
                    >
                      <span>{menuItem.label}</span>
                      {menuItem.children.length > 0 && (
                        <i className="pi pi-angle-down" aria-hidden="true" />
                      )}
                    </button>
                  ))}
              </nav>

              {activeDesktopMenu && (
                <div className="header-mega-menu-wrap">
                  <div className="header-mega-menu">
                    {activeDesktopMenu.children.map((branch) => (
                      <a
                        key={branch.id}
                        href={branch.url}
                        className={`header-mega-link ${
                          activeDesktopBranch?.id === branch.id ? "is-active" : ""
                        }`}
                        onMouseEnter={() =>
                          setActiveDesktopBranchId(
                            branch.children.length > 0 ? branch.id : null
                          )
                        }
                        onFocus={() =>
                          setActiveDesktopBranchId(
                            branch.children.length > 0 ? branch.id : null
                          )
                        }
                      >
                        <span>{branch.label}</span>
                        {branch.children.length > 0 && (
                          <i className="pi pi-angle-right" aria-hidden="true" />
                        )}
                      </a>
                    ))}
                  </div>

                  {activeDesktopBranch?.children?.length > 0 && (
                    <div className="header-mega-submenu">
                      {activeDesktopBranch.children.map((childItem) => (
                        <a
                          key={childItem.id}
                          href={childItem.url}
                          className="header-mega-link header-mega-leaf-link"
                        >
                          <span>{childItem.label}</span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="header-search-slot">{renderSearchBox()}</div>

            <div className="header-actions">
              <a
                className="header-action-button"
                href="/wishlist/"
                aria-label="Wishlist"
                onClick={() => setIsSearchOpen(false)}
              >
                <i className="fa-light fa-heart" />
              </a>

              <div className="header-account-wrapper">
                <button
                  type="button"
                  className="header-action-button"
                  aria-label="Account"
                  onClick={(event) => {
                    setIsSearchOpen(false);
                    accountMenuRef.current?.toggle(event);
                  }}
                >
                  <i className="fa-light fa-user" />
                </button>

                <Menu
                  model={accountMenuItems}
                  popup
                  popupAlignment="right"
                  appendTo="self"
                  ref={accountMenuRef}
                  className="header-account-menu"
                />
              </div>

              <a
                className="header-action-button header-cart-button"
                href="/cart/"
                aria-label="Cart"
                onClick={() => setIsSearchOpen(false)}
              >
                <i className="fa-light fa-cart-shopping" />
                <span className="header-cart-count">0</span>
              </a>
            </div>
          </div>
        </div>
      </header>

      <Sidebar
        visible={isSidebarVisible}
        position="left"
        onHide={() => setIsSidebarVisible(false)}
        showCloseIcon={false}
        blockScroll
        className="header-mobile-sidebar"
        header={
          <div className="header-mobile-sidebar-top">
            <span className="header-mobile-sidebar-title">Menu</span>
            <button
              type="button"
              className="header-mobile-sidebar-close"
              onClick={() => setIsSidebarVisible(false)}
              aria-label="Close navigation menu"
            >
              <i className="pi pi-times" />
            </button>
          </div>
        }
      >
        <div className="header-mobile-sidebar-body">
          <Link className="header-mobile-logo" to="/" onClick={() => setIsSidebarVisible(false)}>
            <img src="/assets/images/logo.png" alt="Hambler" />
          </Link>

          <PanelMenu
            model={panelMenuItems}
            multiple
            expandIcon="pi pi-angle-down"
            collapseIcon="pi pi-angle-up"
            className="header-mobile-panelmenu"
          />
        </div>
      </Sidebar>
    </>
  );
}

export default Header;
